/**
 * @file Frontend error types.
 *
 * Error classes for frontend HTTP and network operations.
 * All extend CoreError for consistent error handling.
 *
 * Per-class fields (status, timeout, details, …) live in `metadata` so they
 * are part of the canonical structured-context channel that flows through
 * `toJSON()`, log sinks, and `getErrorChain()`. The class still exposes a
 * typed accessor for ergonomic call sites.
 */

import { CoreError } from '../shared/index.ts'

////////////////////////////////////////////////////////////////////////////////
// HTTP Error
////////////////////////////////////////////////////////////////////////////////

/**
 * Error from HTTP response with status >= 400.
 *
 * - status: HTTP status code (in metadata)
 * - body: Response body (in metadata, if provided)
 * - url: Full request URL (in metadata, if provided)
 */
export class HttpError extends CoreError {
  constructor(status: number, body?: string, url?: string) {
    super({
      code: 'HTTP_ERROR',
      message: `HTTP ${status}${body ? `: ${body}` : ''}`,
      metadata: {
        status,
        ...(body !== undefined && { body }),
        ...(url !== undefined && { url }),
      },
    })
  }

  get status(): number {
    return this.metadata.status as number
  }

  get body(): string | undefined {
    return this.metadata.body as string | undefined
  }

  get url(): string | undefined {
    return this.metadata.url as string | undefined
  }
}

////////////////////////////////////////////////////////////////////////////////
// Network Error
////////////////////////////////////////////////////////////////////////////////

/**
 * Error from network failure (offline, DNS, connection refused, etc.).
 */
export class NetworkError extends CoreError {
  constructor(cause?: Error) {
    super({
      code: 'NETWORK_ERROR',
      message: cause?.message ?? 'Network error',
      cause,
    })
  }
}

////////////////////////////////////////////////////////////////////////////////
// Timeout Error
////////////////////////////////////////////////////////////////////////////////

/**
 * Error from request timeout.
 *
 * - timeout: Duration in milliseconds (in metadata)
 */
export class TimeoutError extends CoreError {
  constructor(timeout: number) {
    super({
      code: 'TIMEOUT_ERROR',
      message: `Request timed out after ${timeout}ms`,
      metadata: { timeout },
    })
  }

  get timeout(): number {
    return this.metadata.timeout as number
  }
}

////////////////////////////////////////////////////////////////////////////////
// Validation Error
////////////////////////////////////////////////////////////////////////////////

/**
 * Error from response schema validation failure.
 *
 * - details: Validation error details (in metadata, if provided)
 */
export class ValidationError extends CoreError {
  constructor(message: string, details?: unknown) {
    super({
      code: 'VALIDATION_ERROR',
      message,
      metadata: details !== undefined ? { details } : {},
    })
  }

  get details(): unknown {
    return this.metadata.details
  }
}
